import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  Card,
  Group,
  Loader,
  NumberInput,
  Select,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  Textarea,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconAlertCircle, IconCheck } from '@tabler/icons-react';
import { PageHeader } from '../../shared/components/PageHeader';
import { ApiError } from '../../shared/api/problem';
import { usePermissions } from '../auth/usePermissions';
import { useCurrentWarehouseId } from '../warehouses/currentWarehouse';
import { listItemUoms, previewStockingQuantity } from '../items/api';
import {
  ADJUSTMENT_GROUP_CODE,
  listTransactionCodes,
  transactionCodeLabel,
} from '../transactionCodes/api';
import {
  postAdjustment,
  scanItem,
  scanLocation,
  type ScannedItemDto,
  type ScannedLocationDto,
} from './api';

/** Legacy's Location column is varchar(15) and its Reference varchar(20). */
const LOCATION_MAX = 15;
const REFERENCE_MAX = 20;

interface Search {
  /** Prefills the item, so the inquiry screen's Adjust button lands here ready to go. */
  itemNumber?: string;
  warehouseId?: string;
}

/**
 * Inventory Adjustment — reached straight from the menu, and worked with a scanner.
 *
 * This replaced a dialog that could only be opened from the inventory inquiry screen after picking
 * an item, and which asked for a Balance Category and a mandatory Reason. That is a slower path
 * than the current product's for no gain, and it loses the thing that makes the current product's
 * screen good: an operator standing at a rack can do the whole job without looking at the screen.
 *
 * So the field order, the validate-on-leave behaviour and the button set are the current product's
 * (Save / Reset / Done). One field is gone and one is no longer mandatory:
 *
 *   Balance Category  GONE. The stock category belongs to the LOCATION. The current product keeps
 *                     it on the location row and every one of its inquiries rolls up by it, so QC
 *                     stock is stock in a QC location. Asking for both gives them a way to
 *                     disagree. The scan reports the category it resolved, so it is visible
 *                     without being a question.
 *
 *   Reason            OPTIONAL, and last on the form. The current product's screen has no reason
 *                     field at all — it has Reference, which this has too — and the transaction
 *                     code already says why. Kept because an explanation is worth having when
 *                     somebody has one to give, but nobody is stopped by it (sponsor decision,
 *                     2026-08-17).
 *
 * WHAT MAKES IT SCANNABLE
 * Each scan field validates when you leave it, and a bad scan clears the field and takes focus back
 * rather than letting you carry on and fail at Save — the current product does exactly this. A
 * barcode ends with Enter, so Enter resolves the field and moves on instead of submitting the form:
 * a scanner fired at this screen walks itself down the page.
 */
export function AdjustRoute() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const permissions = usePermissions();
  const search = useSearch({ strict: false }) as Search;

  const canAdjust = permissions.has('inventory:adjust');

  // ---- Fields -------------------------------------------------------------
  const [tranCode, setTranCode] = useState('');
  const [reference, setReference] = useState('');
  const [locationText, setLocationText] = useState('');
  const [itemText, setItemText] = useState('');
  const [lotNumber, setLotNumber] = useState('');
  const [serialNumber, setSerialNumber] = useState('');
  const [quantity, setQuantity] = useState<number | ''>('');
  const [uomId, setUomId] = useState('');
  const [reason, setReason] = useState('');
  /** yyyy-mm-dd, straight from a native date input. */
  const [lotExpiration, setLotExpiration] = useState('');

  // ---- What the scans resolved to ----------------------------------------
  const [resolvedLocation, setResolvedLocation] = useState<ScannedLocationDto | null>(null);
  const [resolvedItem, setResolvedItem] = useState<ScannedItemDto | null>(null);
  const [locationError, setLocationError] = useState<string | null>(null);
  const [itemError, setItemError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [scanningLocation, setScanningLocation] = useState(false);
  const [scanningItem, setScanningItem] = useState(false);
  /** Set by a lot_needs_expiration refusal, which reveals the date field. */
  const [needsExpiration, setNeedsExpiration] = useState(false);

  // ---- Starting a new license plate --------------------------------------
  // The scanned code was not a location and not a plate. On a code that ADDS stock that is often
  // not a mis-scan at all — it is somebody holding a fresh pallet label. The current product asks
  // rather than refuses, and builds the plate as part of the posting, the same way an unregistered
  // lot registers itself.
  /** The unknown code, held while the operator is asked what it is. */
  const [pendingPlate, setPendingPlate] = useState<string | null>(null);
  /** They said yes: this is the plate being started, and it needs somewhere to stand. */
  const [newPlateNumber, setNewPlateNumber] = useState('');
  const [plateHomeText, setPlateHomeText] = useState('');
  const [plateHome, setPlateHome] = useState<ScannedLocationDto | null>(null);
  const [plateHomeError, setPlateHomeError] = useState<string | null>(null);
  const [scanningPlateHome, setScanningPlateHome] = useState(false);
  /**
   * Asked once per entry. Saying no means "that was a typo", and asking again on the next
   * mis-scan would turn a slip into an interrogation — the current product sets the same latch.
   */
  const [platePromptDeclined, setPlatePromptDeclined] = useState(false);

  const tranCodeRef = useRef<HTMLInputElement>(null);
  const locationRef = useRef<HTMLInputElement>(null);
  const plateHomeRef = useRef<HTMLInputElement>(null);
  const itemRef = useRef<HTMLInputElement>(null);
  const quantityRef = useRef<HTMLInputElement>(null);
  const lotRef = useRef<HTMLInputElement>(null);
  const serialRef = useRef<HTMLInputElement>(null);

  // The header names it. This screen used to show a picker when a tenant had more than one
  // warehouse, which meant somebody could be scanning bins in one building while the menu and
  // their permissions belonged to another. Legacy has no warehouse field on this screen at all,
  // for the same reason: you are standing in the warehouse you are counting.
  const warehouseId = useCurrentWarehouseId();

  const tranCodes = useQuery({
    queryKey: ['transaction-codes', ADJUSTMENT_GROUP_CODE] as const,
    queryFn: ({ signal }) => listTransactionCodes({ groupCode: ADJUSTMENT_GROUP_CODE }, signal),
  });

  // Per item, and only once an item has resolved.
  const itemUoms = useQuery({
    queryKey: ['item-uoms', resolvedItem?.itemId ?? 'none'] as const,
    queryFn: ({ signal }) => listItemUoms(resolvedItem!.itemId, signal),
    enabled: Boolean(resolvedItem?.itemId),
  });

  const selectedCode = useMemo(
    () => (tranCodes.data ?? []).find((c) => c.tranCode === tranCode) ?? null,
    [tranCodes.data, tranCode],
  );

  const uomOptions = useMemo(
    () => (itemUoms.data ?? []).map((u) => ({ value: u.uomId, label: `${u.uomCode} — ${u.displayName}` })),
    [itemUoms.data],
  );

  const selectedUom = useMemo(
    () => (itemUoms.data ?? []).find((u) => u.uomId === uomId) ?? null,
    [itemUoms.data, uomId],
  );
  const stockingUom = useMemo(
    () => (itemUoms.data ?? []).find((u) => u.isStockingUom) ?? null,
    [itemUoms.data],
  );

  // Advisory only — the server does the real conversion. Lets the operator see "2,000 EA = 2 CS"
  // while typing instead of taking it on faith.
  const preview = useMemo(() => {
    if (!selectedUom || selectedUom.isStockingUom || quantity === '') return null;
    const converted = previewStockingQuantity(Number(quantity), selectedUom.factor);
    if (converted === null || !stockingUom) return null;
    return { converted, code: stockingUom.uomCode };
  }, [selectedUom, stockingUom, quantity]);

  // ---- Scans --------------------------------------------------------------

  /**
   * Resolves the location field. A failure clears it and takes focus back, which is the current
   * product's behaviour and the reason a mis-scan costs one beep instead of a failed post.
   */
  const runLocationScan = useCallback(
    async (advanceTo?: HTMLInputElement | null) => {
      const code = locationText.trim();
      if (!code) {
        setResolvedLocation(null);
        setLocationError(null);
        return;
      }
      if (resolvedLocation && resolvedLocation.locationCode === code.toUpperCase()) return;

      setScanningLocation(true);
      setLocationError(null);
      try {
        const found = await scanLocation(code, warehouseId || undefined);
        setResolvedLocation(found);
        setLocationText(found.licensePlateNumber ?? found.locationCode);
        advanceTo?.focus();
      } catch (err) {
        setResolvedLocation(null);

        // Not a location and not a plate. On a code that ADDS stock this is often somebody
        // holding a label for a pallet that does not exist yet, so ask rather than refuse —
        // exactly where the current product asks. Only once per entry: a second prompt after a
        // genuine typo is an interrogation.
        const unknown = err instanceof ApiError && err.problem.type?.includes('scan_unknown');
        if (unknown && (selectedCode?.sign ?? 0) > 0 && !platePromptDeclined) {
          setPendingPlate(code.toUpperCase());
          setLocationError(null);
          return;
        }

        setLocationText('');
        setLocationError(
          err instanceof ApiError
            ? (err.problem.detail ?? err.problem.title ?? t('inventory.adjustScreen.locationUnknown'))
            : t('inventory.adjustScreen.locationUnknown'),
        );
        locationRef.current?.focus();
      } finally {
        setScanningLocation(false);
      }
    },
    [locationText, resolvedLocation, warehouseId, selectedCode, platePromptDeclined, t],
  );

  /**
   * Resolves where a new plate is going to stand. It has to be a real location, not another plate:
   * a pallet cannot sit on a pallet.
   */
  const runPlateHomeScan = useCallback(async (advanceTo?: HTMLInputElement | null) => {
    const code = plateHomeText.trim();
    if (!code) {
      setPlateHome(null);
      setPlateHomeError(null);
      return;
    }
    if (plateHome && plateHome.locationCode === code.toUpperCase()) return;

    setScanningPlateHome(true);
    setPlateHomeError(null);
    try {
      const found = await scanLocation(code, warehouseId || undefined);
      if (found.kind === 'license_plate') {
        setPlateHome(null);
        setPlateHomeText('');
        setPlateHomeError(t('inventory.adjustScreen.plateHomeNotAPlate'));
        plateHomeRef.current?.focus();
        return;
      }
      setPlateHome(found);
      setPlateHomeText(found.locationCode);
      advanceTo?.focus();
    } catch (err) {
      setPlateHome(null);
      setPlateHomeText('');
      setPlateHomeError(
        err instanceof ApiError
          ? (err.problem.detail ?? err.problem.title ?? t('inventory.adjustScreen.locationUnknown'))
          : t('inventory.adjustScreen.locationUnknown'),
      );
      plateHomeRef.current?.focus();
    } finally {
      setScanningPlateHome(false);
    }
  }, [plateHomeText, plateHome, warehouseId, t]);

  /** Yes, that is a new plate. Keep the code as its number and ask where it goes. */
  function acceptNewPlate() {
    setNewPlateNumber(pendingPlate ?? '');
    setPendingPlate(null);
    setPlateHomeText('');
    setPlateHome(null);
    setPlateHomeError(null);
    window.setTimeout(() => plateHomeRef.current?.focus(), 0);
  }

  /** No, it was a typo. Clear it, and stop asking for the rest of this entry. */
  function declineNewPlate() {
    setPendingPlate(null);
    setPlatePromptDeclined(true);
    setLocationText('');
    setLocationError(null);
    locationRef.current?.focus();
  }

  /** Backing out of a plate already accepted, without resetting the whole screen. */
  function cancelNewPlate() {
    setNewPlateNumber('');
    setPlateHomeText('');
    setPlateHome(null);
    setPlateHomeError(null);
    setLocationText('');
    locationRef.current?.focus();
  }

  /** Resolves the item, then lays the rest of the form out from what came back. */
  const runItemScan = useCallback(
    async (rawValue?: string) => {
      const number = (rawValue ?? itemText).trim();
      if (!number) {
        setResolvedItem(null);
        setItemError(null);
        return;
      }
      if (resolvedItem && resolvedItem.itemNumber === number) return;

      setScanningItem(true);
      setItemError(null);
      try {
        const found = await scanItem(number);
        setResolvedItem(found);
        setItemText(found.itemNumber);
        // The unit follows the item, as it does in the current product: it sets the combo to the
        // item's own UM the moment the item resolves.
        setUomId(found.primaryUomId);
        // One serial is one physical thing, so there is nothing to decide — the handheld sets the
        // box to 1 and disables it, and this does the same. Anything else is cleared, so the
        // previous entry's quantity cannot ride along onto the next item.
        setQuantity(found.isSerialized ? 1 : '');
        setLotNumber('');
        setSerialNumber('');
        setNeedsExpiration(false);
        setLotExpiration('');
      } catch (err) {
        setResolvedItem(null);
        setItemText('');
        setUomId('');
        setItemError(
          err instanceof ApiError
            ? (err.problem.title ?? t('inventory.adjustScreen.itemUnknown'))
            : t('inventory.adjustScreen.itemUnknown'),
        );
        itemRef.current?.focus();
      } finally {
        setScanningItem(false);
      }
    },
    [itemText, resolvedItem, t],
  );

  // Arriving from the inquiry screen's Adjust button, which passes the number it was showing.
  const prefilled = useRef(false);
  useEffect(() => {
    if (prefilled.current || !search.itemNumber) return;
    prefilled.current = true;
    setItemText(search.itemNumber);
    void runItemScan(search.itemNumber);
  }, [search.itemNumber, runItemScan]);

  useEffect(() => {
    tranCodeRef.current?.focus();
  }, []);

  // ---- Reset --------------------------------------------------------------

  /**
   * Clears everything and goes back to the top, which is what the current product does after a
   * successful Save as well as on Reset. It does mean re-picking the transaction code for a run of
   * identical adjustments; that is the current product's behaviour and changing it here would be a
   * behaviour change nobody asked for.
   */
  const resetScreen = useCallback(() => {
    setTranCode('');
    setReference('');
    setLocationText('');
    setItemText('');
    setLotNumber('');
    setSerialNumber('');
    setQuantity('');
    setUomId('');
    setReason('');
    setLotExpiration('');
    setNeedsExpiration(false);
    setResolvedLocation(null);
    setResolvedItem(null);
    setLocationError(null);
    setItemError(null);
    setSubmitError(null);
    setFieldErrors({});
    setPendingPlate(null);
    setNewPlateNumber('');
    setPlateHomeText('');
    setPlateHome(null);
    setPlateHomeError(null);
    // The latch is per entry, so the next adjustment gets asked again.
    setPlatePromptDeclined(false);
    tranCodeRef.current?.focus();
  }, []);

  // ---- Post ---------------------------------------------------------------

  const mutation = useMutation({
    mutationFn: () =>
      postAdjustment({
        // Starting a plate means the location is the plate's new home, not the thing scanned into
        // the first field — that text is the plate's number.
        warehouseId: (newPlateNumber ? plateHome! : resolvedLocation!).warehouseId,
        itemId: resolvedItem!.itemId,
        locationId: (newPlateNumber ? plateHome! : resolvedLocation!).locationId,
        quantity: Math.abs(Number(quantity)),
        uomId,
        tranCode,
        // No balanceTypeId: the server takes it from the location. That is the point of the rework.
        licensePlateId: newPlateNumber ? null : resolvedLocation!.licensePlateId,
        newLicensePlateNumber: newPlateNumber || null,
        lotNumber: resolvedItem!.isLotControlled ? lotNumber.trim().toUpperCase() : null,
        serialNumber: resolvedItem!.isSerialized ? serialNumber.trim().toUpperCase() : null,
        // Midnight UTC on the chosen day. A lot expires on a date, not at an instant, so anchoring
        // it to the browser's zone would move the date for anybody east or west of the server.
        lotExpirationDateUtc: lotExpiration ? `${lotExpiration}T00:00:00Z` : null,
        reason: reason.trim() || null,
        referenceType: null,
        referenceId: reference.trim() || null,
      }),
    onSuccess: () => {
      notifications.show({
        message: t('inventory.adjustScreen.posted', {
          quantity: Number(quantity).toLocaleString(),
          item: resolvedItem?.itemNumber ?? '',
        }),
        color: 'green',
        icon: <IconCheck size={16} />,
      });
      resetScreen();
    },
    onError: (err) => {
      if (!(err instanceof ApiError)) {
        setSubmitError(t('inventory.errors.adjustFailed'));
        return;
      }
      const type = err.problem.type ?? '';

      // Not a failure so much as a question: the lot is new and the item expires, so show the date
      // field and let them post again. The current product opens a registration dialog here.
      if (type.includes('lot_needs_expiration')) {
        setNeedsExpiration(true);
        setSubmitError(err.problem.detail ?? t('inventory.adjustScreen.lotNeedsExpiration'));
        return;
      }
      if (type.includes('would_go_negative')) {
        setSubmitError(t('inventory.errors.wouldGoNegative'));
        return;
      }
      // These name the place and both numbers, so the server's own detail is the useful text.
      if (
        type.includes('insufficient_at_location') ||
        type.includes('serial_already_on_hand') ||
        type.includes('new_plate_needs_home') ||
        type.includes('new_plate_conflict') ||
        type.includes('new_plate_needs_positive_code')
      ) {
        setSubmitError(err.problem.detail ?? err.problem.title ?? t('inventory.errors.adjustFailed'));
        return;
      }
      if (type.includes('uom_not_associated')) {
        setSubmitError(t('inventory.errors.uomNotAssociated'));
        return;
      }
      if (type.includes('quantity_too_small')) {
        setSubmitError(t('inventory.errors.quantityTooSmall'));
        return;
      }
      setSubmitError(err.problem.detail ?? err.problem.title ?? t('inventory.errors.adjustFailed'));
    },
  });

  function onSave() {
    setSubmitError(null);
    const errors: Record<string, string> = {};
    if (!tranCode) errors.tranCode = t('inventory.errors.tranCodeRequired');
    if (newPlateNumber) {
      if (!plateHome) errors.plateHome = t('inventory.adjustScreen.plateHomeRequired');
    } else if (!resolvedLocation) {
      errors.location = t('inventory.adjustScreen.locationRequired');
    }
    if (!resolvedItem) errors.item = t('inventory.adjustScreen.itemRequired');
    if (!uomId) errors.uom = t('inventory.errors.uomRequired');
    if (quantity === '' || Number(quantity) <= 0) errors.quantity = t('inventory.errors.quantityPositive');
    if (resolvedItem?.isLotControlled && !lotNumber.trim()) {
      errors.lot = t('inventory.adjustScreen.lotRequired');
    }
    if (resolvedItem?.isSerialized && !serialNumber.trim()) {
      errors.serial = t('inventory.adjustScreen.serialRequired');
    }
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;
    mutation.mutate();
  }

  /** A barcode ends with Enter, so Enter resolves the field and moves on rather than submitting. */
  function scanKey(run: () => void) {
    return (e: React.KeyboardEvent) => {
      if (e.key !== 'Enter') return;
      e.preventDefault();
      run();
    };
  }

  if (!canAdjust) {
    return (
      <>
        <PageHeader title={t('inventory.adjustScreen.title')} />
        <Alert color="yellow" variant="light" icon={<IconAlertCircle size={16} />}>
          {t('inventory.adjustScreen.notPermitted')}
        </Alert>
      </>
    );
  }

  return (
    <>
      <PageHeader title={t('inventory.adjustScreen.title')} />

      <Card withBorder maw={720}>
          <Stack gap="md">
            <Select
              ref={tranCodeRef}
              label={t('inventory.tranCode')}
              description={
                selectedCode
                  ? selectedCode.sign > 0
                    ? t('inventory.tranCodeAdds')
                    : t('inventory.tranCodeRemoves')
                  : t('inventory.tranCodeHint')
              }
              placeholder={t('inventory.tranCodePlaceholder')}
              data={(tranCodes.data ?? []).map((c) => ({
                value: c.tranCode,
                label: transactionCodeLabel(c),
              }))}
              value={tranCode || null}
              onChange={(v) => {
                const next = v ?? '';
                setTranCode(next);
                // Switching to a code that removes stock abandons any plate being started: there
                // would be nothing on it to remove, and the server refuses it (50137). Better to
                // drop it here than to let the operator fill in a form that cannot post.
                const sign = (tranCodes.data ?? []).find((c) => c.tranCode === next)?.sign ?? 0;
                if (sign <= 0 && newPlateNumber) cancelNewPlate();
                if (sign <= 0) setPendingPlate(null);
              }}
              error={fieldErrors.tranCode}
              disabled={tranCodes.isLoading}
              searchable
              required
            />

            <TextInput
              label={t('inventory.adjustScreen.reference')}
              description={t('inventory.adjustScreen.referenceHint')}
              value={reference}
              onChange={(e) => setReference(e.currentTarget.value)}
              maxLength={REFERENCE_MAX}
            />

            <TextInput
              ref={locationRef}
              label={t('inventory.adjustScreen.location')}
              description={
                newPlateNumber
                  ? t('inventory.adjustScreen.locationIsNewPlate')
                  : t('inventory.adjustScreen.locationHint')
              }
              placeholder={t('inventory.adjustScreen.locationPlaceholder')}
              value={newPlateNumber || locationText}
              onChange={(e) => setLocationText(e.currentTarget.value.toUpperCase())}
              onBlur={() => void runLocationScan()}
              onKeyDown={scanKey(() => void runLocationScan(itemRef.current))}
              error={fieldErrors.location}
              maxLength={LOCATION_MAX}
              rightSection={scanningLocation ? <Loader size="xs" /> : null}
              disabled={Boolean(newPlateNumber)}
              required
            />

            {/* The current product's wording, near enough: an unknown code on a code that adds
                stock is more often a fresh pallet label than a typo, so it asks. */}
            {pendingPlate && (
              <Alert color="blue" variant="light" icon={<IconAlertCircle size={16} />}>
                <Stack gap="xs">
                  <Text size="sm">
                    {t('inventory.adjustScreen.newPlatePrompt', { code: pendingPlate })}
                  </Text>
                  <Group gap="sm">
                    <Button size="xs" color="invetrak" onClick={acceptNewPlate}>
                      {t('inventory.adjustScreen.newPlateYes')}
                    </Button>
                    <Button size="xs" variant="default" onClick={declineNewPlate}>
                      {t('inventory.adjustScreen.newPlateNo')}
                    </Button>
                  </Group>
                </Stack>
              </Alert>
            )}

            {/* A plate is not a place, so a new one has to be told where it stands. */}
            {newPlateNumber && (
              <>
                <TextInput
                  ref={plateHomeRef}
                  label={t('inventory.adjustScreen.plateHome')}
                  description={t('inventory.adjustScreen.plateHomeHint')}
                  placeholder={t('inventory.adjustScreen.locationPlaceholder')}
                  value={plateHomeText}
                  onChange={(e) => setPlateHomeText(e.currentTarget.value.toUpperCase())}
                  onBlur={() => void runPlateHomeScan()}
                  // Advances only on success, like the location field above. Moving on regardless
                  // would blur a field the failure handler had just cleared and refocused, and the
                  // blur would re-run the scan against an empty box — quietly wiping the error the
                  // operator needed to see.
                  onKeyDown={scanKey(() => void runPlateHomeScan(itemRef.current))}
                  error={fieldErrors.plateHome}
                  maxLength={LOCATION_MAX}
                  rightSection={scanningPlateHome ? <Loader size="xs" /> : null}
                  required
                />

                {plateHomeError && (
                  <Alert color="red" variant="light" icon={<IconAlertCircle size={16} />} py="xs">
                    {plateHomeError}
                  </Alert>
                )}

                {plateHome && (
                  <Text size="sm" c="dimmed">
                    {t('inventory.adjustScreen.newPlateSummary', {
                      plate: newPlateNumber,
                      location: plateHome.locationName ?? plateHome.locationCode,
                      category: plateHome.balanceTypeName,
                    })}
                  </Text>
                )}

                <Group>
                  <Button size="xs" variant="subtle" onClick={cancelNewPlate}>
                    {t('inventory.adjustScreen.newPlateCancel')}
                  </Button>
                </Group>
              </>
            )}

            {locationError && (
              <Alert color="red" variant="light" icon={<IconAlertCircle size={16} />} py="xs">
                {locationError}
              </Alert>
            )}

            {/* What the scan resolved. The category appears here as a FACT, not a question — which
                is the whole difference from the dialog this replaced. */}
            {resolvedLocation && !newPlateNumber && (
              <Text size="sm" c="dimmed">
                {resolvedLocation.kind === 'license_plate'
                  ? t('inventory.adjustScreen.resolvedPlate', {
                      plate: resolvedLocation.licensePlateNumber,
                      location: resolvedLocation.locationCode,
                      category: resolvedLocation.balanceTypeName,
                    })
                  : t('inventory.adjustScreen.resolvedLocation', {
                      name: resolvedLocation.locationName ?? resolvedLocation.locationCode,
                      category: resolvedLocation.balanceTypeName,
                    })}
              </Text>
            )}

            <TextInput
              ref={itemRef}
              label={t('inventory.adjustScreen.itemNumber')}
              description={t('inventory.adjustScreen.itemHint')}
              value={itemText}
              onChange={(e) => setItemText(e.currentTarget.value)}
              onBlur={() => void runItemScan()}
              // Lands on whichever field the item actually needs next. The handheld goes to the lot
              // box after an item resolves; for a serialized item the quantity is fixed, so
              // skipping to the serial is the same idea.
              onKeyDown={scanKey(() => {
                void runItemScan().then(() =>
                  (lotRef.current ?? serialRef.current ?? quantityRef.current)?.focus(),
                );
              })}
              error={fieldErrors.item}
              rightSection={scanningItem ? <Loader size="xs" /> : null}
              required
            />

            {itemError && (
              <Alert color="red" variant="light" icon={<IconAlertCircle size={16} />} py="xs">
                {itemError}
              </Alert>
            )}

            {resolvedItem && (
              <Text size="sm" c="dimmed">
                {resolvedItem.description}
              </Text>
            )}

            {/* Only for items that need them, exactly as the current product reveals its panels. */}
            {resolvedItem?.isLotControlled && (
              <TextInput
                ref={lotRef}
                label={t('inventory.adjustScreen.lotNumber')}
                description={t('inventory.adjustScreen.lotHint')}
                value={lotNumber}
                onChange={(e) => setLotNumber(e.currentTarget.value.toUpperCase())}
                error={fieldErrors.lot}
                required
              />
            )}

            {/* A native date input rather than a picker component: this appears rarely, and the
                platform's own control is the one the operator's device already knows. */}
            {needsExpiration && (
              <TextInput
                type="date"
                label={t('inventory.adjustScreen.lotExpiration')}
                description={t('inventory.adjustScreen.lotExpirationHint')}
                value={lotExpiration}
                onChange={(e) => setLotExpiration(e.currentTarget.value)}
                required
              />
            )}

            {resolvedItem?.isSerialized && (
              <TextInput
                ref={serialRef}
                label={t('inventory.adjustScreen.serialNumber')}
                description={t('inventory.adjustScreen.serialHint')}
                value={serialNumber}
                onChange={(e) => setSerialNumber(e.currentTarget.value.toUpperCase())}
                error={fieldErrors.serial}
                required
              />
            )}

            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
              {/* Fixed at 1 and read-only for a serialized item — one serial is one physical
                  thing, so there is nothing to type. The server enforces it too (50134): a
                  disabled input is a courtesy, not a control. */}
              <NumberInput
                ref={quantityRef}
                label={t('inventory.quantity')}
                description={
                  resolvedItem?.isSerialized
                    ? t('inventory.adjustScreen.quantitySerialized')
                    : t('inventory.quantityHint')
                }
                value={quantity}
                onChange={(v) => setQuantity(v === '' ? '' : Number(v))}
                error={fieldErrors.quantity}
                min={0}
                decimalScale={4}
                disabled={resolvedItem?.isSerialized ?? false}
                required
              />
              {/* Locked to the stocking unit for a serialized item, for the same reason the
                  quantity is locked to 1: one serial number is one unit of the stocking unit, and
                  1 CS on an item that cases in thousands would be a thousand of it. The server
                  enforces this too (50135). */}
              <Select
                label={t('inventory.adjustScreen.um')}
                description={
                  resolvedItem?.isSerialized
                    ? t('inventory.adjustScreen.umSerialized')
                    : preview
                      ? t('inventory.uomPreview', {
                          quantity: preview.converted.toLocaleString(undefined, { maximumFractionDigits: 5 }),
                          uom: preview.code,
                        })
                      : t('inventory.adjustScreen.umHint')
                }
                data={uomOptions}
                value={uomId || null}
                onChange={(v) => setUomId(v ?? '')}
                error={fieldErrors.uom}
                disabled={!resolvedItem || itemUoms.isLoading || resolvedItem.isSerialized}
                allowDeselect={false}
                searchable
                required
              />
            </SimpleGrid>

            {/* Optional, and last: the current product's screen has no reason at all, so putting
                it anywhere in the scanned sequence would add a tab stop to a flow that does not
                have one. Somebody who wants to explain an adjustment types here; nobody is
                stopped by it. (Sponsor decision 2026-08-17 — keep the field, drop the
                requirement.) */}
            <Textarea
              label={t('inventory.reason')}
              description={t('inventory.reasonHint')}
              value={reason}
              onChange={(e) => setReason(e.currentTarget.value)}
              maxLength={200}
              autosize
              minRows={2}
              maxRows={4}
            />

            {submitError && (
              <Alert color="red" variant="light" icon={<IconAlertCircle size={16} />}>
                {submitError}
              </Alert>
            )}

            {/* Save / Reset / Done, in the current product's order. Save stays on the screen and
                clears it for the next one — this is a screen somebody stands at. */}
            <Group gap="sm">
              <Button color="invetrak" onClick={onSave} loading={mutation.isPending}>
                {t('inventory.adjustScreen.save')}
              </Button>
              <Button variant="default" onClick={resetScreen}>
                {t('inventory.adjustScreen.reset')}
              </Button>
              <Button variant="default" onClick={() => void navigate({ to: '/app/inventory' })}>
                {t('inventory.adjustScreen.done')}
              </Button>
            </Group>
          </Stack>
      </Card>
    </>
  );
}
